import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { CalculationStore } from '../../../../../stores/calculation.store';
import { CalculationError } from '../../../../../../../../../../../common/src/errors/calculations/calculation.error';
import { calculationErrorMetadata } from '../../../../../../../../../../../common/src/metadatas/calculation-error.metadata';
import { TranslateService } from '@ngx-translate/core';
import { CalculationResponse } from '../../../../../../../../../../../common/dist/angular/responses/calculation.response';
import { CategoryScale, Chart, LinearScale, LineController, LineElement, PointElement, Tooltip } from 'chart.js';
import { CalculationStatusEnum } from '../../../../../../../../../../../common/src/enums/calculations/calculation-status.enum';
import { MoneyUtil } from '../../../../../../../../../../../common/src/utils/money.util';
import { LocaleService } from '../../../../../../advanced-auth/services/locale.service';
import annotationPlugin from 'chartjs-plugin-annotation';
import { RrspOptions } from '../../../../../../../../../../../common/src/options/calculations/rrsp/rrsp.options';
import { RrspSimulationResult } from '../../../../../../../../../../../common/src/results/calculations/rrsp/rrsp-simulation.result';
import { FormFactory } from '@magieno/angular-advanced-forms';
import { advancedFormControl, FormControlTypeEnum } from '@magieno/common';

class SelectGraphOptions {
  @advancedFormControl({
    items: [
      { id: 'amountGraph', label: "Retour d'impôt" },
      { id: 'ratioGraph', label: 'Pourcentage de retour par dollar investi' },
    ],
    type: FormControlTypeEnum.Select,
  })
  selectedGraph: string = 'amountGraph';
}

@Component({
  selector: 'app-rrsp-result',
  templateUrl: './rrsp-result.component.html',
  styleUrls: ['./rrsp-result.component.scss'],
  standalone: false,
})
export class RrspResultComponent implements OnInit, OnDestroy {
  CalculationStatusEnum = CalculationStatusEnum;
  public calculationId: string;
  public calculationResponse: CalculationResponse;
  public options: RrspOptions;
  public results: RrspSimulationResult;
  selectGraphForm = this.formFactory.create(new SelectGraphOptions());
  isSectionRrspSavingsCollapsed: boolean = false;
  isSectionCorporateDisbursementCollapsed: boolean = false;
  isSectionNote1Collapsed: boolean = false;
  isSectionNote2Collapsed: boolean = false;
  isSectionNote3Collapsed: boolean = false;
  selectedBadge: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
  amountChartElement: HTMLCanvasElement | undefined;
  amountChart: Chart | undefined;
  ratioChartElement: HTMLCanvasElement | undefined;
  ratioChart: Chart | undefined;
  private calculationResponseSubscription: Subscription;

  constructor(
    private readonly calculationStore: CalculationStore,
    private readonly translateService: TranslateService,
    private readonly localeService: LocaleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly formFactory: FormFactory,
  ) {
    // Start by setting the calculationId
    this.calculationId = this.activatedRoute.snapshot.params.calculationId;
  }

  private _calculationStatusEnum: CalculationStatusEnum;

  get calculationStatusEnum(): CalculationStatusEnum {
    return this._calculationStatusEnum;
  }

  set calculationStatusEnum(value: CalculationStatusEnum) {
    this._calculationStatusEnum = value;
  }

  ngOnInit(): void {
    this.activatedRoute.parent.params.subscribe((params) => {
      if (params.calculationId === undefined) {
        return;
      }

      this.calculationId = params.calculationId;

      // Unsubscribe all the subscriptions on route change.
      this.unsubscribeAll();

      this.calculationResponseSubscription = this.calculationStore
        .get(params.calculationId)
        .subscribe(async (calculationResponse) => {
          this.calculationStatusEnum = calculationResponse?.status ?? this.calculationStatusEnum;

          // todo: add a mechanism to know if the results are simply loading (when calculationResponse === undefined) or there's an error (the other two conditions)
          if (
            calculationResponse === undefined ||
            calculationResponse.options === undefined ||
            calculationResponse.results === undefined
          ) {
            return;
          }

          this.calculationResponse = calculationResponse;
          this.options = calculationResponse.options.options as RrspOptions;
          this.results = calculationResponse.results.results as RrspSimulationResult;

          await this.setupGraphContainer();
          await this.initAmountGraph();
          await this.updateAmountGraph();
          await this.initRatioGraph();
          await this.updateRatioGraph();
        });
    });

    // this.calculationResponse = exampleResponse;
    // this.options = this.calculationResponse.options.options as SalaryDividendOptions;
    // this.results = this.calculationResponse.results.results as SalaryDividendResult;
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  public async setupGraphContainer(): Promise<void> {
    window.addEventListener('beforeprint', () => {
      document.getElementById('graph-container')!.style.width = '825px';

      // HIDE CHAT BOX ON PRINT
      const liveFrame = document.querySelector('#hubspot-messages-iframe-container > iframe');
      if (liveFrame != null) {
        // @ts-ignore
        liveFrame.style.setProperty('display', 'none', 'important');
      }
    });
    window.addEventListener('afterprint', () => {
      document.getElementById('graph-container')!.style.width = 'auto';

      const liveFrame = document.querySelector('#hubspot-messages-iframe-container > iframe');
      if (liveFrame != null) {
        // @ts-ignore
        liveFrame.style.removeProperty('display');
      }
    });
  }

  public async initAmountGraph() {
    if (this.amountChartElement !== undefined) {
      return;
    }

    Chart.register(annotationPlugin);
    Chart.register(Tooltip);
    Chart.register(CategoryScale);
    Chart.register(LinearScale);
    Chart.register(LineController);
    Chart.register(PointElement);
    Chart.register(LineElement);

    this.amountChartElement = document.getElementById('amount-graph') as HTMLCanvasElement;
    this.amountChart = new Chart(this.amountChartElement, {
      type: 'line',
      data: {
        datasets: [],
      },
      options: {
        scales: {
          y: {
            title: {
              display: true,
              text: "Retour d'impôt",
            },
          },
          x: {
            title: {
              display: true,
              text: 'Contribution',
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return (context.raw as any).description;
              },
            },
          },
        },
      },
    });
  }

  public async updateAmountGraph() {
    const numberFormat = new Intl.NumberFormat(await this.localeService.getLocaleForUser(), {
      style: 'currency',
      currency: 'CAD',
      currencyDisplay: 'narrowSymbol',
    });

    const graphData = (this.calculationResponse.results.results as RrspSimulationResult).allOtherPoints.sort((a, b) => {
      return a.percentage - b.percentage;
    });

    this.amountChart.data.labels = graphData.map((item) => {
      return MoneyUtil.centsToDollars(item.contributionAmount).toString();
    });

    this.amountChart.data.datasets = [
      {
        borderColor: '#1C7ED6',
        pointBackgroundColor: '#005098',
        data: graphData.map((item) => {
          return {
            x: MoneyUtil.centsToDollars(item.contributionAmount),
            y: MoneyUtil.centsToDollars(item.taxReturn),
            description: `Contribution: ${numberFormat.format(MoneyUtil.centsToDollars(item.contributionAmount))}, Retour: ${MoneyUtil.centsToDollars(item.taxReturn)})}`,
          };
        }),
      },
    ];

    this.amountChart?.update();
  }

  public async initRatioGraph() {
    if (this.ratioChartElement !== undefined) {
      return;
    }

    Chart.register(annotationPlugin);
    Chart.register(Tooltip);
    Chart.register(CategoryScale);
    Chart.register(LinearScale);
    Chart.register(LineController);
    Chart.register(PointElement);
    Chart.register(LineElement);

    this.ratioChartElement = document.getElementById('ratio-graph') as HTMLCanvasElement;
    this.ratioChart = new Chart(this.ratioChartElement, {
      type: 'line',
      data: {
        datasets: [],
      },
      options: {
        scales: {
          y: {
            title: {
              display: true,
              text: 'Pourcentage de retour par dollar investi',
            },
          },
          x: {
            title: {
              display: true,
              text: 'Contribution',
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return (context.raw as any).description;
              },
            },
          },
        },
      },
    });
  }

  public async updateRatioGraph() {
    const numberFormat = new Intl.NumberFormat(await this.localeService.getLocaleForUser(), {
      style: 'currency',
      currency: 'CAD',
      currencyDisplay: 'narrowSymbol',
    });

    const graphData = (this.calculationResponse.results.results as RrspSimulationResult).allOtherPoints.sort((a, b) => {
      return a.percentage - b.percentage;
    });

    this.ratioChart.data.labels = graphData.map((item) => {
      return MoneyUtil.centsToDollars(item.contributionAmount).toString();
    });

    this.ratioChart.data.datasets = [
      {
        borderColor: '#1C7ED6',
        pointBackgroundColor: '#005098',
        data: graphData.map((item) => {
          return {
            x: MoneyUtil.centsToDollars(item.contributionAmount),
            y: MoneyUtil.centsToDollars(item.amountOfTaxesSavedPerDollar),
            description: `Contribution: ${numberFormat.format(MoneyUtil.centsToDollars(item.contributionAmount))}, Pourcentage de retour par dollar investi: ${numberFormat.format(MoneyUtil.centsToDollars(item.amountOfTaxesSavedPerDollar))})}`,
          };
        }),
      },
    ];

    this.ratioChart?.update();
  }

  getErrorText(error: string | CalculationError) {
    if (typeof error === 'string') {
      return error;
    }

    if (error.hasOwnProperty('calculationErrorKeyname')) {
      const calculationError = calculationErrorMetadata[error.calculationErrorKeyname];

      if (calculationError === undefined) {
        return 'Erreur inconnue'; // todo: translate this.
      }

      if (
        calculationError.i18n.hasOwnProperty(this.translateService.currentLang) &&
        calculationError.i18n[this.translateService.currentLang] !== ''
      ) {
        return calculationError.i18n[this.translateService.currentLang];
      }

      return calculationError.i18n.fr;
    }
  }

  private unsubscribeAll() {
    this.calculationResponseSubscription?.unsubscribe();
  }
}
